import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { integrityStyles } from "./integrityTheme";
import { pillars, offers, offersByPillar, method, CONTACT_EMAIL } from "./offers";
import { setMeta, setJsonLd, SITE_URL, breadcrumbJsonLd } from "./seo";
import SiteFooter from "./SiteFooter";

export default function Services() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setMeta({
      title: "Capabilities — Documore Integrity",
      description:
        "Documore applies one discipline, decision integrity, across three domains: the records a business runs on, the documents its decisions depend on, and the AI systems that act on its behalf.",
      url: `${SITE_URL}/services`,
    });
    const cleanups = [
      setJsonLd("services", {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Documore Integrity capabilities",
        itemListElement: offers.map((o, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: o.name,
          url: `${SITE_URL}/services/${o.slug}`,
        })),
      }),
      setJsonLd("breadcrumb", breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Capabilities", path: "/services" },
      ])),
    ];
    return () => cleanups.forEach((c) => c());
  }, []);

  return (
    <>
      <style>{integrityStyles}</style>
      <div className="di-root">
        <div className="di-wrap">
          <div className="di-hero">
            <div>
              <div className="di-kicker">
                <span className="di-kicker-line" />
                <span className="di-kicker-text">The Documore Integrity Practice</span>
              </div>
              <h1 className="di-h1">Decisions are only as sound as <em>the data beneath them.</em></h1>
            </div>
            <p className="di-lede">
              Documore applies one discipline, decision integrity, across three domains: the records a business runs
              on, the documents its decisions depend on, and the AI systems that now act on its behalf. Every
              engagement verifies agreement across sources, isolates each mismatch, and documents the evidence, so
              action is taken on what is true.
            </p>
          </div>

          <section className="di-section" style={{ paddingTop: 16 }}>
            <hr className="di-rule" />
            <div className="di-kicker" style={{ marginTop: 40 }}>
              <span className="di-kicker-line" />
              <span className="di-kicker-text">One method</span>
            </div>
            <div className="di-grid-3">
              {method.map((m) => (
                <div key={m.num} className="di-cell">
                  <div className="di-method-num">{m.num}</div>
                  <h3 className="di-h3" style={{ marginBottom: 10 }}>{m.title}</h3>
                  <p className="di-text" style={{ fontSize: 15 }}>{m.text}</p>
                </div>
              ))}
            </div>
          </section>

          {pillars.map((p) => (
            <section key={p.key} id={p.key} className="di-section">
              <hr className="di-rule" />
              <div style={{ padding: "40px 0 24px", display: "grid", gap: "8px" }}>
                <div className="di-kicker" style={{ marginBottom: 4 }}>
                  <span className="di-kicker-line" />
                  <span className="di-kicker-text">{p.name}</span>
                </div>
                <h2 className="di-h2">{p.promise}</h2>
                <p className="di-text" style={{ maxWidth: 760 }}>{p.description}</p>
              </div>
              <div className={offersByPillar(p.key).length === 4 ? "di-grid-2" : "di-grid-3"}>
                {offersByPillar(p.key).map((o) => (
                  <Link key={o.slug} to={`/services/${o.slug}`} className="di-card">
                    <span className="di-h3">{o.name}</span>
                    <span className="di-text" style={{ fontSize: 14 }}>{o.summary}</span>
                    <span className="di-card-more" style={{ marginTop: "auto", paddingTop: 8 }}>View capability →</span>
                  </Link>
                ))}
              </div>
            </section>
          ))}

          <section className="di-section" style={{ paddingTop: 16 }}>
            <hr className="di-rule" />
            <div style={{ padding: "40px 0 0", display: "grid", gap: 16, maxWidth: 760 }}>
              <h2 className="di-h2">Beyond these capabilities</h2>
              <p className="di-text">
                Wherever systems, documents or AI must agree before a decision is made, the same method applies.
                Describe the decision and the sources behind it, and we will define how it can be verified.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a className="di-btn" href="/#contact">Discuss your case</a>
                <a className="di-btn-ghost" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </div>
            </div>
          </section>
        </div>
        <SiteFooter />
      </div>
    </>
  );
}
